/* eslint-disable no-console,@typescript-eslint/no-throw-literal,react/jsx-no-useless-fragment */

import { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  changeConfiguration,
  changeDirectSalesPricing,
  changeFeatures,
  changeCampaignConfiguration,
} from 'store/publisher/reducer';
import {
  getDirectSalesPublisherPricing,
  getPublisherConfiguration,
  getPublisherFeatures,
  getDirectSalesPublisherCampaignConfig,
} from 'modules/api/publisher';
import { mergePublisherAndEnvironmentFeatures, normalizeFeatureKeys } from 'utils/mergePublisherAndEnvironmentFeatures';
import { cancelFunctions } from 'utils/cancelFunctions';
import { getDealConfig, getDealManagementConfig } from 'modules/api/programmaticDeal';
import { updateDealConfig } from 'store/dealConfig/reducer';

interface AppConfigLoaderProps {
  isAuthenticated: boolean;
  isAuthChecked: boolean;
  environmentId: string;
  isComponentReadyToRender: boolean;
  suspendWhileLoading?: boolean;
}

export const AppConfigLoader: React.FC<PropsWithChildren<AppConfigLoaderProps>> = ({
  children,
  isAuthenticated,
  isAuthChecked,
  environmentId,
  isComponentReadyToRender,
  suspendWhileLoading = true,
}) => {
  const [loadingPromise, setLoadingPromise] = useState<Promise<void> | null>(null);
  const loadedEnvironmentIdRef = useRef<string | null>(null);

  const dispatch = useDispatch();

  const hasEnvironmentChanged =
    loadedEnvironmentIdRef.current !== null && loadedEnvironmentIdRef.current !== environmentId;
  const shouldLoadConfig =
    Boolean(environmentId && isAuthenticated && isAuthChecked) && (!isComponentReadyToRender || hasEnvironmentChanged);

  const changeAppConfig = async (): Promise<void> => {
    const configuration = await getPublisherConfiguration({ envId: environmentId, cancelFunctions });
    dispatch(changeConfiguration(configuration));

    const publisherFeatures = await getPublisherFeatures({ envId: environmentId, cancelFunctions });
    const normalizedFeatures = normalizeFeatureKeys(publisherFeatures);
    const features = mergePublisherAndEnvironmentFeatures(environmentId, normalizedFeatures);
    dispatch(changeFeatures(features));

    const publisherPricing = await getDirectSalesPublisherPricing({
      envId: environmentId,
      cancelFunctions,
    });

    const publisherCampaignConfig = await getDirectSalesPublisherCampaignConfig({
      envId: environmentId,
      cancelFunctions,
    });

    dispatch(changeDirectSalesPricing(publisherPricing));
    dispatch(changeCampaignConfiguration(publisherCampaignConfig));

    const dealConfig = await getDealConfig(environmentId, cancelFunctions);
    const dealManagementConfig = await getDealManagementConfig(environmentId, cancelFunctions);

    const mappedBillingRules = dealManagementConfig?.applicationConfiguration?.billingRules?.split(',');
    dispatch(updateDealConfig({ ...dealConfig, billingRules: ['Test Deal', ...(mappedBillingRules || [])] }));
  };

  useEffect(() => {
    if (!shouldLoadConfig || loadingPromise) {
      return;
    }

    const startLoading = async (): Promise<void> => {
      try {
        await changeAppConfig();
        loadedEnvironmentIdRef.current = environmentId;
      } catch (error) {
        console.error(`Changing publisher configuration error: ${error}`);
        throw error;
      } finally {
        setLoadingPromise(null);
      }
    };

    setLoadingPromise(startLoading());
  }, [shouldLoadConfig, environmentId]);

  if (loadingPromise && suspendWhileLoading) {
    throw loadingPromise;
  }

  return <>{children}</>;
};

/* eslint-enable no-console,@typescript-eslint/no-throw-literal,react/jsx-no-useless-fragment */
